if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('[data-submit-label]');
        this.submitButtonPriceGroup = this.submitButton.querySelector('[data-submit-price-group]');
        this.submitButtonPrice = this.submitButton.querySelector('[data-submit-price]');
        this.productInfo = this.closest('product-info');
        this.priceSource = this.productInfo?.querySelector(`[id^="price-"]`);
        this.initialButtonPrice = this.submitButton.dataset.initialPrice || '';
        this.submitButtonCompare = this.submitButton.querySelector('[data-submit-compare]');
        this.submitButtonCompareValue = this.submitButton.querySelector('[data-submit-compare-value]');
        this.handlePriceMutation = this.handlePriceMutation.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        // Selling-plan pricing is opt-in: only forms that render the price map take part.
        this.planPriceMap = this.readPlanPriceMap();
        this.selectedSellingPlanId = null;
        this.onSellingPlanInteraction = this.onSellingPlanInteraction.bind(this);
        this.handlePlanMutation = this.handlePlanMutation.bind(this);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        /*
         * The theme's own subscription-widget block renders its own button and owns
         * its pricing (subscription price + struck one-time price). Syncing from the
         * #price- block here would overwrite that price and hide the struck one on
         * load, so this form opts out of button-price syncing entirely.
         */
        this.ownsSubmitPrice = !this.closest('subscription-widget');

        if (this.ownsSubmitPrice) {
          this.setupPriceSync();
          this.syncSubmitButtonPrice();
          requestAnimationFrame(() => {
            this.setupPriceSync(true);
            this.syncSubmitButtonPrice();
          });
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);

        if (!this.planPriceMap) return;

        // The subscription app block renders inside <product-info>, so these bubbling
        // listeners cover it wherever it is placed among the blocks.
        this.sellingPlanEventTarget = this.productInfo || document;
        ['change', 'input', 'click'].forEach((eventName) => {
          this.sellingPlanEventTarget.addEventListener(eventName, this.onSellingPlanInteraction);
        });

        this.setupSellingPlanObserver();
        this.refreshSellingPlanPrice();
        // The app widget boots after the theme, so re-read once the first frame is done.
        requestAnimationFrame(() => this.refreshSellingPlanPrice());
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.planObserver?.disconnect();
        this.variantChangeUnsubscriber?.();

        if (this.sellingPlanEventTarget) {
          ['change', 'input', 'click'].forEach((eventName) => {
            this.sellingPlanEventTarget.removeEventListener(eventName, this.onSellingPlanInteraction);
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.toggleSubmitButtonPrice(false);
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButton.dataset.addToCartLabel || window.variantStrings.addToCart;
          this.syncSubmitButtonPrice();
        }
      }

      setupPriceSync(forceRefresh = false) {
        if (forceRefresh || !this.priceSource) {
          this.priceSource = this.productInfo?.querySelector(`[id^="price-"]`);
        }

        if (!this.priceSource) return;

        this.priceObserver?.disconnect();
        this.priceObserver = new MutationObserver(this.handlePriceMutation);
        this.priceObserver.observe(this.priceSource, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }

      handlePriceMutation() {
        this.syncSubmitButtonPrice();
      }

      /*
       * ── Selling-plan pricing ────────────────────────────────────────────────
       * When a subscription widget (Appstle app block, or any widget that uses
       * input[name="selling_plan"]) has a plan selected, the button shows that
       * plan's price for the current variant instead of the one-time price from
       * the #price- block. Everything below is inert unless buy-buttons.liquid
       * rendered the price map, so the theme's own subscription-widget block —
       * which owns its own button — is untouched.
       */

      readPlanPriceMap() {
        const source = this.querySelector('script[data-plan-price-map]');
        if (!source) return null;

        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          console.error('Invalid selling plan price map', error);
          return null;
        }
      }

      onSellingPlanInteraction(event) {
        if (this.isOwnMutation(event.target)) return;
        this.requestSellingPlanRefresh();
      }

      handlePlanMutation(mutations) {
        if (mutations.every((mutation) => this.isOwnMutation(mutation.target))) return;
        this.requestSellingPlanRefresh();
      }

      // Ignore anything we wrote ourselves, and anything belonging to the theme's
      // own subscription widget — neither should drive this button's price.
      isOwnMutation(node) {
        const element = node instanceof Element ? node : node?.parentElement;
        if (!element) return false;

        return Boolean(
          element.closest('subscription-widget') ||
            element.closest('[data-submit-price-group]') ||
            element.closest('[data-theme-selling-plan]')
        );
      }

      requestSellingPlanRefresh() {
        if (this.pendingSellingPlanRefresh) return;

        this.pendingSellingPlanRefresh = true;
        requestAnimationFrame(() => {
          this.pendingSellingPlanRefresh = false;
          this.refreshSellingPlanPrice();
        });
      }

      setupSellingPlanObserver() {
        this.planObserver?.disconnect();
        this.planObserver = new MutationObserver(this.handlePlanMutation);

        const observerOptions = {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'checked', 'selected', 'disabled', 'class', 'data-selected-selling-plan'],
        };

        const appBlocks = Array.from(this.productInfo?.querySelectorAll('.shopify-app-block') || []);
        const targets = appBlocks.length ? appBlocks : [this.productInfo];
        targets.push(this.form);

        targets.forEach((target) => target && this.planObserver.observe(target, observerOptions));
      }

      getPlansForCurrentVariant() {
        return this.planPriceMap?.[this.variantIdInput?.value]?.plans || {};
      }

      // Widget-agnostic: matches on the field name, never on app class names.
      getSellingPlanFields() {
        const scope = this.productInfo || document;

        return Array.from(scope.querySelectorAll('[name*="selling_plan"]')).filter(
          (field) => !field.closest('subscription-widget') && !field.hasAttribute('data-theme-selling-plan')
        );
      }

      getSelectedSellingPlanId() {
        const normalize = (value) => {
          const id = (value ?? '').toString().trim();
          return !id || id === '0' || id === 'none' ? null : id;
        };

        const fields = this.getSellingPlanFields();
        const radios = fields.filter((field) => field.type === 'radio');

        if (radios.length) {
          const checked = radios.find((radio) => radio.checked);
          // An empty value is the widget's explicit "one-time purchase" option.
          const planId = normalize(checked?.value);
          if (!planId) return null;

          // The frequency dropdown next to the subscribe option is authoritative
          // when it disagrees with the radio (Appstle updates them separately).
          return this.getFrequencyPlanId(checked) || planId;
        }

        const select = fields.find((field) => field.tagName === 'SELECT');
        if (select) return normalize(select.value);

        const input = fields.find((field) => !field.disabled);
        if (input) return normalize(input.value);

        const widget = this.productInfo?.querySelector('[data-selected-selling-plan]');
        return normalize(widget?.getAttribute('data-selected-selling-plan'));
      }

      // Walks up from the checked radio looking for a <select> whose value is a real
      // selling plan for the current variant — validated against the price map, so no
      // unrelated dropdown can be mistaken for the frequency picker.
      getFrequencyPlanId(radio) {
        const plans = this.getPlansForCurrentVariant();
        const boundary = this.productInfo || document.body;
        let node = radio.parentElement;

        while (node && node !== boundary) {
          const select = node.querySelector('select');
          if (select && plans[select.value]) return select.value;
          node = node.parentElement;
        }

        return null;
      }

      refreshSellingPlanPrice() {
        if (!this.planPriceMap) return;

        this.selectedSellingPlanId = this.getSelectedSellingPlanId();
        this.syncSellingPlanInput();
        this.syncSubmitButtonPrice();
      }

      getSelectedPlanPrice() {
        if (!this.selectedSellingPlanId) return null;
        return this.getPlansForCurrentVariant()[this.selectedSellingPlanId] || null;
      }

      /*
       * The app widget renders outside this <form>, so its selection never reaches
       * new FormData(this.form). Mirror the resolved plan into a hidden field, or the
       * button would show a subscription price while adding a one-time item.
       */
      syncSellingPlanInput() {
        const existing = this.form.querySelector(
          '[name="selling_plan"]:not([disabled]):not([data-theme-selling-plan])'
        );
        const plan = existing ? null : this.getSelectedPlanPrice();

        if (!plan) {
          if (this.mirroredPlanInput) this.mirroredPlanInput.disabled = true;
          return;
        }

        if (!this.mirroredPlanInput) {
          this.mirroredPlanInput = document.createElement('input');
          this.mirroredPlanInput.type = 'hidden';
          this.mirroredPlanInput.name = 'selling_plan';
          this.mirroredPlanInput.setAttribute('data-theme-selling-plan', '');
          this.form.appendChild(this.mirroredPlanInput);
        }

        this.mirroredPlanInput.value = this.selectedSellingPlanId;
        this.mirroredPlanInput.disabled = false;
      }

      setSubmitButtonCompare(priceText) {
        if (!this.submitButtonCompare || !this.submitButtonCompareValue) return;

        if (priceText) this.submitButtonCompareValue.textContent = priceText;
        this.submitButtonCompare.classList.toggle('hidden', !priceText);
      }

      onVariantChange({ data }) {
        if (data?.sectionId !== this.dataset.sectionId) return;

        requestAnimationFrame(() => {
          this.priceSource = this.productInfo?.querySelector(`[id^="price-"]`);
          this.setupPriceSync();
          // Re-resolve the plan first: the new variant may price it differently, or
          // may not be part of the selling plan group at all.
          this.selectedSellingPlanId = this.planPriceMap ? this.getSelectedSellingPlanId() : null;
          this.syncSellingPlanInput();
          const nextPrice = this.getCurrentDisplayedPrice(data?.html);
          this.syncSubmitButtonPrice(nextPrice);
        });
      }

      getPriceFromNode(node) {
        if (!node) return '';

        const selectors = [
          '.price__sale .price-item--sale',
          '.price__regular .price-item--regular',
          '.price-item--regular',
          '.price-item--sale',
        ];

        for (const selector of selectors) {
          const priceElement = node.querySelector(selector);
          if (priceElement && !priceElement.closest('.hidden')) {
            const priceText = priceElement.textContent.replace(/\s+/g, ' ').trim();
            if (priceText) return priceText;
          }
        }

        return '';
      }

      getCurrentDisplayedPrice(sourceDocument) {
        if (sourceDocument) {
          const sourcePriceNode = sourceDocument.getElementById?.(`price-${this.dataset.sectionId}`);
          const sourcePriceText = this.normalizeButtonPrice(this.getPriceFromNode(sourcePriceNode));
          if (sourcePriceText) return sourcePriceText;
        }

        const currentPriceText = this.normalizeButtonPrice(this.getPriceFromNode(this.priceSource));
        if (currentPriceText) return currentPriceText;

        return this.normalizeButtonPrice(this.submitButtonPrice?.textContent?.trim() || this.initialButtonPrice);
      }

      normalizeButtonPrice(priceText) {
        if (!priceText) return '';

        return priceText
          .replace(/\s+/g, ' ')
          .replace(/([.,]00)(?!\d)/, '')
          .replace(/\s[A-Z]{3,4}$/, '')
          .trim();
      }

      syncSubmitButtonPrice(priceOverride) {
        if (this.ownsSubmitPrice === false) return;
        if (!this.submitButtonPrice || !this.submitButtonPriceGroup) return;

        if (!this.priceSource) {
          this.setupPriceSync(true);
        }

        const isDisabled =
          this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-disabled') === 'true';
        const currentLabel = this.submitButtonText?.textContent?.trim();
        const soldOutLabel = this.submitButton.dataset.soldOutLabel;
        const unavailableLabel = this.submitButton.dataset.unavailableLabel;

        if (isDisabled && (currentLabel === soldOutLabel || currentLabel === unavailableLabel)) {
          this.setSubmitButtonCompare(null);
          this.toggleSubmitButtonPrice(false);
          return;
        }

        /*
         * A selected selling plan wins over every other price source. Both the
         * #price- MutationObserver and onVariantChange funnel through here, so this
         * branch keeps the subscription price from being overwritten by the one-time
         * price without needing any locking between the two.
         */
        const plan = this.getSelectedPlanPrice();
        if (plan) {
          // Normalized so plan prices read exactly like the one-time price does.
          this.submitButtonPrice.textContent = this.normalizeButtonPrice(plan.price);
          this.setSubmitButtonCompare(
            plan.compareAmount > plan.amount ? this.normalizeButtonPrice(plan.compare) : null
          );
          this.toggleSubmitButtonPrice(true);
          return;
        }

        this.setSubmitButtonCompare(null);

        const priceText = priceOverride || this.getCurrentDisplayedPrice();
        if (!priceText) {
          return;
        }

        this.submitButtonPrice.textContent = priceText;
        this.toggleSubmitButtonPrice(true);
      }

      toggleSubmitButtonPrice(shouldShow) {
        this.submitButtonPriceGroup?.classList.toggle('hidden', !shouldShow);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
